import { prisma, type Prisma, BookableItemType, FloorPlan } from "client";
import { createBookableItem } from "../bookableItem";
import { DEFAULT_FLOOR_PLAN_SETTINGS } from "@sajilni/config/constants/floor-plan";

const getNotDeletedWhereClause = (
  condition:
    | Prisma.FloorPlanScalarWhereInput
    | Prisma.FloorPlanWhereUniqueInput
    | undefined
): Prisma.FloorPlanScalarWhereInput => {
  return {
    ...condition,
    // In Mongo via Prisma, equals null matches both null and field not present
    deletedAt: null,
  };
};

/**
 * Get all floor plans
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findMany arguments
 * @returns Array of floor plans
 */
export async function getAllFloorPlans(
  userId: string,
  payload?: Prisma.FloorPlanFindManyArgs
) {
  try {
    const condition = getNotDeletedWhereClause(payload?.where);
    return await prisma.floorPlan.findMany({
      ...payload,
      where: condition,
    });
  } catch (error: any) {
    throw new Error(`Failed to get floor plans: ${error.message}`);
  }
}

/**
 * Get floor plans by event ID
 * @param eventId - Event ID
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findMany arguments
 * @returns Array of floor plans
 */
export async function getFloorPlansByEventId(
  eventId: string,
  userId: string,
  payload?: Prisma.FloorPlanFindManyArgs
) {
  try {
    const condition = getNotDeletedWhereClause({ eventId });
    return await prisma.floorPlan.findMany({
      where: condition,
      ...payload,
    });
  } catch (error: any) {
    throw new Error(`Failed to get floor plans: ${error.message}`);
  }
}

/**
 * Get floor plans by venue ID
 * @param venueId - Venue ID
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findMany arguments
 * @returns Array of floor plans
 */
export async function getFloorPlanByVenueId(
  venueId: string,
  userId?: string,
  payload?: Prisma.FloorPlanFindManyArgs
) {
  try {
    // await checkPermissions(userId, ResourceType.FLOOR_PLAN, ActionType.LIST);
    const condition = getNotDeletedWhereClause({ venueId });
    return await prisma.floorPlan.findMany({
      where: condition,
      ...payload,
    });
  } catch (error: any) {
    throw new Error(`Failed to get floor plans: ${error.message}`);
  }
}

/**
 * Get a floor plan by ID
 * @param id - Floor plan ID
 * @param userId - User ID for permission checking
 * @param payload - Optional Prisma findUnique arguments
 * @returns Floor plan or null if not found
 */
export async function getFloorPlanById(
  id: string,
  userId: string,
  payload?: Omit<Prisma.FloorPlanFindUniqueArgs, "where">
) {
  try {
    const condition = getNotDeletedWhereClause({
      id,
    }) as Prisma.FloorPlanWhereUniqueInput;
    return await prisma.floorPlan.findFirst({
      where: condition,
      ...payload,
    });
  } catch (error: any) {
    throw new Error(`Failed to get floor plan: ${error.message}`);
  }
}

/**
 * Create a new floor plan with default EntityTier
 * @param payload - Floor plan data
 * @param userId - User ID for permission checking
 * @returns Created floor plan with default tier
 */
export async function createFloorPlan(
  payload: Omit<Prisma.FloorPlanCreateInput, "defaultEntityTier" | "settings">,
  eventId: string,
  userId: string,
  venueId: string
) {
  try {
    if (!eventId) {
      throw new Error("Event ID is required to create floor plan");
    }

    // Check if name already exists for this event
    const condition = getNotDeletedWhereClause({
      name: payload.name,
      eventId,
      venueId,
    });
    const isNameExist = await prisma.floorPlan.findFirst({
      where: condition,
      select: { id: true },
    });

    if (isNameExist?.id) {
      throw new Error("Floor plan name already exists for this event");
    }

    // Use transaction to ensure floor plan and default tier are created atomically
    return await prisma.$transaction(async (tx) => {
      // First create a BookableItem for the default tier
      const bookableItem = await createBookableItem(
        {
          type: BookableItemType.ENTITY_TIER,
          event: {
            connect: { id: eventId },
          },
          price: 0,
          minAmount: 1,
          soldTogether: 1,
          // the admin should make it false
          showWhenSoldOut: true,
        },
        tx
      );
      const uniqueTierName = `Default Tier - ${payload.name}`;

      // Step 1: Create the EntityTier WITHOUT floorPlanId first
      // const defaultTier = await tx.entityTier.create({
      //   data: {
      //     name: uniqueTierName,
      //     isDefault: true,
      //     bookableItem: {
      //       connect: { id: bookableItem.id },
      //     },
      //     event: {
      //       connect: { id: eventId },
      //     },
      //   },
      // });

      const {
        fillColor,
        textColor,
        buyerUnavailableFillColor,
        buyerUnavailableTextColor,
        opacity,
        textOpacity,
        availableColor,
        onHoldColor,
        reservedColor,
        blockedColor,
      } = DEFAULT_FLOOR_PLAN_SETTINGS;

      // Step 2: Create the FloorPlan with the defaultEntityTier
      const floorPlan = await tx.floorPlan.create({
        data: {
          ...payload,
          settings: DEFAULT_FLOOR_PLAN_SETTINGS,
          entityTiers: {
            create: {
              name: uniqueTierName,
              isDefault: true,
              settings: {
                fillColor,
                textColor,
                buyerUnavailableFillColor,
                buyerUnavailableTextColor,
                opacity,
                textOpacity,
                availableColor,
                onHoldColor,
                reservedColor,
                blockedColor,
              },
              bookableItem: {
                connect: { id: bookableItem.id },
              },
              event: {
                connect: { id: eventId },
              },
            },
          },
          // include: {
          //   defaultEntityTier: {
          //     include: {
          //       bookableItem: true,
          //     },
          //   },
          // },
        },
      });

      return floorPlan;
    });
  } catch (error: any) {
    throw new Error(`Failed to create floor plan: ${error.message}`);
  }
}

/**
 * Update an existing floor plan
 * @param id - Floor plan ID
 * @param payload - Update data
 * @param userId - User ID for permission checking
 * @returns Updated floor plan
 */
export async function updateFloorPlan(
  id: string,
  payload: Prisma.FloorPlanUpdateInput,
  userId: string
) {
  try {
    if (payload.name) {
      const condition = getNotDeletedWhereClause({
        name: payload.name.toString(),
        eventId: payload.event?.connect?.id,
      });
      const isNameExist = await prisma.floorPlan.findFirst({
        where: condition,
      });
      if (isNameExist) {
        throw new Error("floor name exist for this event");
      }
    }

    return await prisma.floorPlan.update({
      where: { id },
      data: payload,
    });
  } catch (error: any) {
    console.error("FloorPlan update failed:", error);
    throw new Error(`Failed to update floor plan: ${error.message}`);
  }
}

/**
 * Delete a floor plan (soft delete)
 * Soft deletes the floor plan and all related entities, entity tiers, and bookable items
 * @param id - Floor plan ID
 * @param userId - User ID for permission checking
 * @returns Deleted floor plan
 */
export async function deleteFloorPlan(id: string, userId: string) {
  try {
    // Get floor plan with minimal data
    const floorPlan = await prisma.floorPlan.findUnique({
      where: { id },
      select: {
        id: true,
        eventId: true,
        deletedAt: true,
        entityTiers: {
          where: { deletedAt: null },
          select: {
            id: true,
            bookableItemId: true,
          },
        },
        entities: {
          where: { deletedAt: null },
          select: {
            id: true,
            bookableItemId: true,
          },
        },
      },
    });

    if (!floorPlan) {
      throw new Error("Floor plan not found");
    }

    if (floorPlan.deletedAt) {
      throw new Error("Floor plan is already deleted");
    }
    return await prisma.$transaction(async (tx) => {
      const now = new Date();

      // Prepare deletion promises for entities and tiers (independent operations)
      const deletionPromises: Promise<any>[] = [];

      // Soft delete all entities
      if (floorPlan.entities.length > 0) {
        const entityIds: string[] = [];
        const entityBookableItemIds: string[] = [];

        floorPlan.entities.forEach((e) => {
          entityIds.push(e.id);
          if (e.bookableItemId) entityBookableItemIds.push(e.bookableItemId);
        });

        const entityDeletion = tx.entity.updateMany({
          where: { id: { in: entityIds }, deletedAt: null },
          data: { deletedAt: now },
        });

        const entityBookableItemDeletion =
          entityBookableItemIds.length > 0
            ? tx.bookableItem.updateMany({
                where: { id: { in: entityBookableItemIds }, deletedAt: null },
                data: { deletedAt: now },
              })
            : Promise.resolve();

        deletionPromises.push(
          Promise.all([entityDeletion, entityBookableItemDeletion])
        );
      }

      // Soft delete all entity tiers
      if (floorPlan.entityTiers.length > 0) {
        const tierIds: string[] = [];
        const tierBookableItemIds: string[] = [];

        floorPlan.entityTiers.forEach((t) => {
          tierIds.push(t.id);
          tierBookableItemIds.push(t.bookableItemId);
        });

        const tierDeletion = tx.entityTier.updateMany({
          where: { id: { in: tierIds }, deletedAt: null },
          data: { deletedAt: now },
        });

        const tierBookableItemDeletion =
          tierBookableItemIds.length > 0
            ? tx.bookableItem.updateMany({
                where: { id: { in: tierBookableItemIds }, deletedAt: null },
                data: { deletedAt: now },
              })
            : Promise.resolve();

        deletionPromises.push(
          Promise.all([tierDeletion, tierBookableItemDeletion])
        );
      }

      // soft delete entities
      deletionPromises.push(
        tx.entity.updateMany({
          where: { floorPlanId: id, deletedAt: null },
          data: { deletedAt: now },
        })
      );

      // soft delete shapes
      deletionPromises.push(
        tx.shape.updateMany({
          where: {
            floorPlanId: id,
            deletedAt: null,
          },
          data: { deletedAt: now },
        })
      );

      // Soft delete floor plan
      deletionPromises.push(
        tx.floorPlan.update({
          where: { id },
          data: { deletedAt: now },
        })
      );

      // Wait for all soft deletes to complete in parallel
      await Promise.all(deletionPromises);

      return floorPlan;
    });
  } catch (error: any) {
    throw new Error(`Failed to delete floor plan: ${error.message}`);
  }
}
